import React, { useState, useEffect } from 'react';
import { getAppearance } from '../utils/api';

// #rrggbb + alpha → 8-digit hex (used for the dim / glow accent variants)
const withAlpha = (hex, alpha) => {
  if (typeof hex === 'string' && /^#([0-9a-fA-F]{6})$/.test(hex.trim())) return hex.trim() + alpha;
  return hex;
};

// Apply an accent color app-wide by overriding the CSS variables on :root.
// Inline vars win over the [data-theme] stylesheet rules, so the accent stays
// consistent in both light and dark mode.
export function applyAccent(hex) {
  if (!hex) return;
  const root = document.documentElement;
  root.style.setProperty('--accent', hex);
  root.style.setProperty('--accent-dim', withAlpha(hex, '20'));
  root.style.setProperty('--accent-glow', withAlpha(hex, '44'));
}

// Mounted once at the app root: applies the admin accent and shows the single
// active notification popup (once per browser session).
const Appearance = () => {
  const [notif, setNotif] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const a = await getAppearance();
        if (!alive) return;
        if (a.themeAccent) applyAccent(a.themeAccent);

        // Popup is for the public website only — never inside the admin panel.
        const params = new URLSearchParams(window.location.search);
        const isAdmin = process.env.REACT_APP_ADMIN_KEY && params.get('key') === process.env.REACT_APP_ADMIN_KEY;

        const n = a.notification;
        if (!isAdmin && n && n.enabled && n.id) {
          const freq = n.frequency === 'every' ? 'every' : 'once';
          // 'once'  → remembered forever (until admin republishes → new id)
          // 'every' → shown on every visit / reload (no persistence)
          const alreadySeen = localStorage.getItem('robo-notif-seen') === n.id;
          if (freq === 'every' || !alreadySeen) {
            setNotif(n);
            setShow(true);
          }
        }
      } catch { /* backend down — keep defaults, no popup */ }
    })();
    return () => { alive = false; };
  }, []);

  const close = () => {
    // Remember only for "once" mode; "every" always re-shows next visit.
    if (notif?.id && notif.frequency !== 'every') localStorage.setItem('robo-notif-seen', notif.id);
    setShow(false);
  };

  if (!show || !notif) return null;

  return (
    <div className="np-overlay" onClick={close}>
      <div className="np-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="np-header">
          <h3 className="np-title">{notif.title || 'Announcement'}</h3>
          <button className="np-close" onClick={close} aria-label="Close">×</button>
        </div>
        <div className="np-body">{notif.body}</div>
        <div className="np-footer">
          <span className="np-footer-note">{notif.footer || ''}</span>
          <button className="np-btn" onClick={close}>{notif.buttonText || 'Got it'}</button>
        </div>
      </div>
    </div>
  );
};

export default Appearance;
